import AddToQueueIcon from '@mui/icons-material/AddToQueue';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material';
import DeleteButtonWithConfirmation from 'components/DeleteWidthConfirmationHoc';
import _ from 'lodash';
import { FC, useState } from 'react';
import { APP_STRUCTURE } from '../../../../../../../@constants/ACL/app_structure';
import { usePermissionsManagerContext } from '../context';
import { FormActionTypeEnum, RecordPermissionObjectModel } from '../models';
import TreeView from './TreeView';

/**
 *
 */
const TreeViewItem: FC<{
  data: RecordPermissionObjectModel;
  parentPath: string;
}> = ({ data, parentPath }) => {
  const { data: originalData, removeMutation, setStateDialog } = usePermissionsManagerContext();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  const nbrParents = parentPath !== '' ? parentPath.split('.').length : 0;
  const paddingLeft = nbrParents <= 0 ? 1 : nbrParents * 4;
  const currentPath = parentPath !== '' ? `${parentPath}.${data.code}` : data.code;

  const refObject = _.get(
    APP_STRUCTURE,
    [...currentPath.split('.').join('.children.').split('.')],
    {}
  );

  const storedObject = _.get(
    originalData,
    [...currentPath.split('.').join('.children.').split('.')],
    {}
  );

  const nbrRefChildren = Object.keys(refObject?.children ?? {}).length;
  const nbrStoredChildren = Object.keys(storedObject?.children ?? {}).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-start'}
        sx={{
          backgroundColor: nbrParents === 0 ? 'colors.gray.o20' : '#fff',
          pl: paddingLeft,
          pr: 0.5,
          ...(!open ? { borderBottom: 'solid 1px #fff' } : {}),
          cursor: 'pointer',
          ':hover': {
            backgroundColor: nbrParents === 0 ? 'colors.gray.o20' : 'colors.gray.o10',
          },
        }}
        onClick={() => handleClick()}
      >
        <Typography variant="inter14" sx={{ borderLeft: 'dashed 2px #ddd', pl: 2 }}>
          {data.name}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ position: 'relative', mr: 3 }}>
          <Typography
            variant="inter10"
            color={'colors.gray.o40'}
          >{`${nbrStoredChildren} / ${nbrRefChildren} `}</Typography>
        </Box>

        <Box sx={{ width: 195 }}>
          <Stack
            direction={'row'}
            alignItems={'center'}
            divider={<Divider orientation="vertical" flexItem />}
            spacing={1}
          >
            <DeleteButtonWithConfirmation
              isLoading={false}
              onDelete={() => {
                removeMutation.mutate(data.id!);
              }}
              icon={<DeleteOutlineOutlinedIcon />}
            />

            <IconButton
              onClick={e => {
                e.stopPropagation();
                setStateDialog(prevState => ({
                  ...prevState,
                  actionType: FormActionTypeEnum.UPDATE,
                  open: true,
                  defaultValues: {
                    id: data.id,
                    parentPath: currentPath,
                    name: data.name,
                    code: data.code,
                    dependenciesApiPermissions: data?.dependenciesApiPermissions ?? [],
                  },
                }));
              }}
            >
              <EditOutlinedIcon />
            </IconButton>

            <IconButton
              disabled={nbrRefChildren === nbrStoredChildren}
              onClick={e => {
                e.stopPropagation();
                setStateDialog(prevState => ({
                  ...prevState,
                  actionType: FormActionTypeEnum.ADD,
                  open: true,
                  defaultValues: {
                    parentPath: currentPath,
                    name: '',
                    code: '',
                    dependenciesApiPermissions: [],
                  },
                }));
              }}
            >
              <AddToQueueIcon />
            </IconButton>

            {(Object.values(data?.children ?? {}) || []).length > 0 ? (
              <IconButton onClick={() => handleClick()}>
                {open ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            ) : (
              <Box />
            )}
          </Stack>
        </Box>
      </Stack>
      {(Object.values(data?.children ?? {}) || []).length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <TreeView data={data?.children!} parentPath={currentPath} />
        </Collapse>
      )}
    </Box>
  );
};

export default TreeViewItem;
